import { useSyncExternalStore, type ComponentType } from 'react';

/** Subset of Angular `MatDialogConfig` the app uses. */
export interface DialogOptions<D = unknown> {
  data?: D | null;
  width?: string;
  maxWidth?: string;
  height?: string;
  panelClass?: string | string[];
  disableClose?: boolean;
  position?: { top?: string; bottom?: string; left?: string; right?: string };
}

export interface DialogRef<R = unknown> {
  /** Closes the dialog; `afterClosed()` resolves with `result` (undefined when dismissed). */
  close(result?: R): void;
  afterClosed(): Promise<R | undefined>;
}

/** What a dialog component receives (Angular: MAT_DIALOG_DATA and MatDialogRef). `data` is null when none was given. */
export interface DialogComponentProps<D = unknown, R = unknown> {
  data: D | null;
  dialogRef: DialogRef<R>;
}

export interface OpenDialog {
  id: number;
  open: boolean;
  component: ComponentType<DialogComponentProps<never, never>>;
  options: DialogOptions<unknown>;
  ref: DialogRef<unknown>;
}

let nextId = 1;
let dialogs: OpenDialog[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  dialogs = [...dialogs];
  listeners.forEach((listener) => listener());
}

/**
 * Angular `MatDialog.open(Component, config)`. Backdrop click and Escape dismiss it (result undefined) unless
 * `disableClose`. Render <DialogHost /> once, near the app root.
 */
export function openDialog<D = unknown, R = unknown>(
  component: ComponentType<DialogComponentProps<D, R>>,
  options: DialogOptions<D> = {},
): DialogRef<R> {
  let resolve!: (value: R | undefined) => void;
  const closed = new Promise<R | undefined>((r) => (resolve = r));
  const id = nextId++;

  const ref: DialogRef<R> = {
    close(result?: R) {
      const entry = dialogs.find((d) => d.id === id);
      if (!entry || !entry.open) return;
      entry.open = false;
      emit();
      resolve(result);
    },
    afterClosed: () => closed,
  };

  dialogs.push({
    id,
    open: true,
    component: component as OpenDialog['component'],
    options: options as DialogOptions<unknown>,
    ref: ref as DialogRef<unknown>,
  });
  emit();
  return ref;
}

/** Called by the host once the exit transition has finished. */
export function removeDialog(id: number): void {
  dialogs = dialogs.filter((d) => d.id !== id);
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useDialogs(): readonly OpenDialog[] {
  return useSyncExternalStore(
    subscribe,
    () => dialogs,
    () => dialogs,
  );
}

/** Test helper: forget every dialog. */
export function resetDialogs(): void {
  dialogs = [];
  listeners.forEach((listener) => listener());
}
